/**
 * Copyright (c) 2024 Epinics (PVT) LTD
 * All Rights Reserved.
 * <p>
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * <p>
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * <p>
 */
import React, { useState, useEffect } from "react";
import styles from "./index.module.css";
import { Circles } from "react-loading-icons";
import CircularProgress from "@mui/material/CircularProgress";

import Top from "./Top";
import StepOneForm from "./StepOneForm";
import Slider from "./Slider";
import NextBackButtons from "./NextBackButtons";
import StepTwoForm from "./StepTwoForm";
import StepThreeForm from "./StepThreeForm";

// Redux
import {
  sampleAction,
  setStepOneDetails,
  setStepTwoDetails,
  submitDetails,
  validateBusinessName,
} from "../../actions";
import { connect } from "react-redux";
import PropTypes from "prop-types";

const StepsSection = ({
  sampleAction,
  setStepOneDetails,
  setStepTwoDetails,
  submitDetails,
  validateBusinessName,
  layout: {
    sampleState,
    companyName,
    mobileNumber,
    email,
    address,
    password,
    isLoading,
    isSubmitted,
  },
}) => {
  const [step, setStep] = useState(1);

  // Step One State and Handlers -------------
  const [formValuesStepOne, setFormValuesStepOne] = useState({
    companyName: "",
    mobileNumber: "",
    email: "",
    address: "",
  });

  const [formErrorsStepOne, setFormErrorsStepOne] = useState({
    companyName: "",
    mobileNumber: "",
    email: "",
    address: "",
  });

  const [isValidFormStepOne, setIsValidFormStepOne] = useState(false);
  const [isFormFilledStepOne, setIsFormFilledStepOne] = useState(false);
  const [isValidBusiness, setIsValidBusiness] = useState(false);

  const validateFormStepOne = (type, event) => {
    // Get current value
    const value = event.target.value;

    // Define the error object with current errors
    const errors = { ...formErrorsStepOne }; // Copy current errors

    // Define patterns for validation
    const companyNamePattern = /^(?:\S+\s+\S+)/;
    const mobileNumberPattern = /^(?:7|0|(?:\+94))[0-9]{9,10}$/;
    // const addressPattern = /^(?:\S+\s+\S+)/;

    // Validate based on field type
    switch (type) {
      case "companyName":
        errors.companyName = companyNamePattern.test(value)
          ? ""
          : "Please add a valid company name.";
        break;
      case "mobileNumber":
        errors.mobileNumber = mobileNumberPattern.test(value)
          ? ""
          : "Please enter a valid 10-digit mobile number.";
        break;

      default:
        break;
    }

    setFormErrorsStepOne(errors);
    const isValid = Object.values(errors).every(error => error === "");
    setIsValidFormStepOne(isValid);
    const isAllReqFieldsFilled =
      formValuesStepOne.companyName.trim() !== "" &&
      formValuesStepOne.mobileNumber.trim() !== "";

    setIsFormFilledStepOne(isAllReqFieldsFilled);
  };

  const handleOnChangeStepOne = (type, event) => {
    const { id, value } = event.target;
    setFormValuesStepOne(prevValues => ({
      ...prevValues,
      [id]: value,
    }));
    validateFormStepOne(type, event);
  };

  const handleOnClickStepOne = async () => {
    if (isValidFormStepOne && isFormFilledStepOne) {
      setStepOneDetails(formValuesStepOne);
    }
  };

  const handleBusinessNameValidation = async () => {
    const response = await validateBusinessName(
      formValuesStepOne.companyName.trim(),
      formValuesStepOne.mobileNumber.trim()
    );

    return response;
  };

  // Step Two State and Handlers -------------

  const [formValuesStepTwo, setFormValuesStepTwo] = useState({
    rootUserName: "",
    rootUserUsername: "",
    rootUserEmail: null,
    rootUserMobileNumber: null,
    password: "",
    confirmPassword: "",
  });

  const [formErrorsStepTwo, setFormErrorsStepTwo] = useState({
    rootUserName: "",
    rootUserUsername: "",
    rootUserEmail: "",
    rootUserMobileNumber: "",
    password: "",
    confirmPassword: "",
  });

  const [isValidFormStepTwo, setIsValidFormStepTwo] = useState(false);
  const [isFormFilledStepTwo, setIsFormFilledStepTwo] = useState(false);

  const validateFormStepTwo = (type, event) => {
    // Get current value from the event
    const value = event.target.value;

    // Initialize the errors object
    const errors = { ...formErrorsStepTwo };

    // Define the password pattern
    const passwordPattern = /^.{6,}$/;
    const usernamePattern = /^[a-zA-Z0-9_]{3,}$/;

    // Validate based on field type
    switch (type) {
      case "password":
        if (!passwordPattern.test(value)) {
          errors.password = "Password must be at least 6 characters long.";
        } else {
          // Clear password error if valid
          errors.password = "";
        }
        break;

      case "confirmPassword":
        if (value !== formValuesStepTwo.password) {
          errors.confirmPassword = "Passwords do not match.";
        } else {
          // Clear confirmPassword error if valid
          errors.confirmPassword = "";
        }
        break;

      case "rootUserUsername":
        if (!usernamePattern.test(value)) {
          errors.rootUserUsername =
            "Username must be at least 3 characters long and can include only letters, numbers, and underscores .";
        } else {
          errors.rootUserUsername = "";
        }
        break;

      default:
        break;
    }

    // Update the form errors and validate form
    setFormErrorsStepTwo(errors);
    const isValid = Object.values(errors).every(error => error === "");
    setIsValidFormStepTwo(isValid);
    const isAllReqFieldsFilled =
      formValuesStepTwo.password.trim() !== "" &&
      formValuesStepTwo.confirmPassword.trim() !== "" &&
      formValuesStepTwo.rootUserUsername.trim() !== "";

    setIsFormFilledStepTwo(isAllReqFieldsFilled);
  };

  const handleOnChangeStepTwo = (type, event) => {
    const { id, value } = event.target;
    setFormValuesStepTwo(prevValues => ({
      ...prevValues,
      [id]: value,
    }));
    validateFormStepTwo(type, event);
  };

  const handleOnClickStepTwo = () => {
    if (isValidFormStepTwo && isFormFilledStepTwo) {
      setStepTwoDetails(formValuesStepTwo);
      submitDetails({
        companyName: companyName,
        mobileNumber: mobileNumber,
        email: email,
        address: address,
        rootUserName: formValuesStepTwo.rootUserName,
        rootUserUsername: formValuesStepTwo.rootUserUsername,
        rootUserEmail: formValuesStepTwo.rootUserEmail,
        rootUserMobileNumber: formValuesStepTwo.rootUserMobileNumber,
        password: formValuesStepTwo.password,
      });
    }
  };

  useEffect(() => {}, [isValidFormStepTwo]);

  return (
    <div className={styles.mainDiv}>
      <div className={styles.container}>
        <Top step={step} setStep={setStep} />
        {isLoading ? (
          <div className={styles.bottomDiv}>
            <div className={styles.loadingDiv}>
              {" "}
              <CircularProgress />
            </div>
          </div>
        ) : (
          <div className={styles.bottomDiv}>
            {step === 1 ? (
              <StepOneForm
                step={step}
                setStep={setStep}
                formValuesStepOne={formValuesStepOne}
                formErrorsStepOne={formErrorsStepOne}
                isValidFormStepOne={isValidFormStepOne}
                validateFormStepOne={validateFormStepOne}
                handleOnChangeStepOne={handleOnChangeStepOne}
                handleOnClickStepOne={handleOnClickStepOne}
                isFormFilledStepOne={isFormFilledStepOne}
              />
            ) : step === 2 ? (
              <StepTwoForm
                step={step}
                setStep={setStep}
                formValuesStepTwo={formValuesStepTwo}
                formErrorsStepTwo={formErrorsStepTwo}
                isValidFormStepTwo={isValidFormStepTwo}
                validateFormStepTwo={validateFormStepTwo}
                handleOnChangeStepTwo={handleOnChangeStepTwo}
                handleOnClickStepTwo={handleOnClickStepTwo}
                isFormFilledStepTwo={isFormFilledStepTwo}
              />
            ) : (
              <StepThreeForm step={step} setStep={setStep} />
            )}

            <NextBackButtons
              step={step}
              setStep={setStep}
              handleOnClickStepOne={handleOnClickStepOne}
              handleOnClickStepTwo={handleOnClickStepTwo}
              isValidFormStepOne={isValidFormStepOne}
              isValidFormStepTwo={isValidFormStepTwo}
              isFormFilledStepOne={isFormFilledStepOne}
              isFormFilledStepTwo={isFormFilledStepTwo}
              handleBusinessNameValidation={handleBusinessNameValidation}
            />
            <Slider step={step} setStep={setStep} />
          </div>
        )}
      </div>
    </div>
  );
};

StepsSection.propTypes = {
  sampleAction: PropTypes.func.isRequired,
  setStepOneDetails: PropTypes.func.isRequired,
  setStepTwoDetails: PropTypes.func.isRequired,
  submitDetails: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  layout: state.layoutState,
});

export default connect(mapStateToProps, {
  sampleAction,
  setStepOneDetails,
  setStepTwoDetails,
  submitDetails,
  validateBusinessName,
})(StepsSection);
